import {t} from 'i18next';
import {type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {Location} from 'react-router';
import * as ReactRouter from 'react-router';

// Deep comparison utility for objects
const deepEquals = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;

  if (typeof a === 'object') {
    if (Array.isArray(a) !== Array.isArray(b)) return false;

    if (Array.isArray(a)) {
      if (a.length !== b.length) return false;
      return a.every((item, index) => deepEquals(item, b[index]));
    }

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;

    return keysA.every((key) => keysB.includes(key) && deepEquals(a[key], b[key]));
  }

  return false;
};

type UseConfirmUnsavedChangesOptions = {
  isDirty?: boolean;
  isDirtyRef?: MutableRefObject<boolean>;
};

export const useConfirmUnsavedChanges = (
  valuesToTrack: any[] = [],
  options: UseConfirmUnsavedChangesOptions = {}
) => {
  const [trackedIsDirty, setTrackedIsDirty] = useState(false);
  const originalValuesRef = useRef<any[]>([]);
  const internalIsDirtyRef = useRef(false);
  const isInitializedRef = useRef(false);
  const initializationTimeoutRef = useRef<NodeJS.Timeout>();
  const translatedUnsavedChangesPrompt = t('unsaved_changes_leave_confirmation');
  const unsavedChangesPrompt =
    typeof translatedUnsavedChangesPrompt === 'string' && translatedUnsavedChangesPrompt
      ? translatedUnsavedChangesPrompt
      : 'You have unsaved changes. Are you sure you want to leave?';
  const isExplicitlyTracked = typeof options.isDirty === 'boolean' || Boolean(options.isDirtyRef);
  const isDirty = options.isDirty ?? trackedIsDirty;

  useEffect(() => {
    internalIsDirtyRef.current = isDirty;
  }, [isDirty]);

  const getIsDirty = useCallback(
    () => options.isDirtyRef?.current ?? internalIsDirtyRef.current,
    [options.isDirtyRef]
  );

  // Memoize the values to prevent unnecessary re-renders
  const memoizedValues = useMemo(
    () => valuesToTrack,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // Create dependencies from the actual values to avoid array reference issues
      // eslint-disable-next-line react-hooks/exhaustive-deps
      ...valuesToTrack,
    ]
  );

  // Store original values with a delay to allow initial state to settle
  useEffect(() => {
    if (isExplicitlyTracked) {
      return undefined;
    }

    if (!isInitializedRef.current) {
      // Clear any existing timeout
      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
      }

      // Set original values after a brief delay to allow state to settle
      initializationTimeoutRef.current = setTimeout(() => {
        originalValuesRef.current = [...memoizedValues];
        isInitializedRef.current = true;
      }, 100); // 100ms delay to allow initial state to settle
    }

    return () => {
      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
      }
    };
  }, [isExplicitlyTracked, memoizedValues]);

  // Check if any values have changed using deep comparison
  useEffect(() => {
    if (isExplicitlyTracked) {
      return;
    }

    if (!isInitializedRef.current || originalValuesRef.current.length === 0) return;

    const hasChanges = memoizedValues.some((currentValue, index) => {
      const originalValue = originalValuesRef.current[index];
      return !deepEquals(currentValue, originalValue);
    });

    setTrackedIsDirty(hasChanges);
  }, [isExplicitlyTracked, memoizedValues]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (getIsDirty()) {
        // Modern browsers require preventDefault
        event.preventDefault();
        const message = unsavedChangesPrompt;
        // Legacy browsers may require setting returnValue
        // See https://developer.mozilla.org/en-US/docs/Web/API/BeforeUnloadEvent/returnValue
        // eslint-disable-next-line no-param-reassign, deprecation/deprecation
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [getIsDirty, unsavedChangesPrompt]);

  // Block React Router navigation when there are unsaved changes
  ReactRouter.useBlocker(
    useCallback(
      ({currentLocation, nextLocation}: {currentLocation: Location; nextLocation: Location}) => {
        // Don't block if we're not dirty
        if (!getIsDirty()) {
          return false;
        }

        // Don't block if we're navigating to the same location (including search/hash)
        if (
          currentLocation.pathname === nextLocation.pathname &&
          currentLocation.search === nextLocation.search &&
          currentLocation.hash === nextLocation.hash
        ) {
          return false;
        }

        // Show confirmation dialog
        return !window.confirm(unsavedChangesPrompt);
      },
      [getIsDirty, unsavedChangesPrompt]
    )
  );

  const confirmUnsavedChanges = useCallback(() => {
    if (getIsDirty()) {
      return window.confirm(unsavedChangesPrompt);
    }
    return true;
  }, [getIsDirty, unsavedChangesPrompt]);

  const resetDirtyState = useCallback(() => {
    setTrackedIsDirty(false);
    // Update original values to current values
    originalValuesRef.current = [...memoizedValues];
    isInitializedRef.current = true; // Ensure we're marked as initialized
  }, [memoizedValues]);

  return {
    confirmUnsavedChanges,
    resetDirtyState,
  };
};
